import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  IconButton,
  Box,
  Chip,
  Avatar,
  Grid
} from '@mui/material';
import { Star as StarIcon, Person as PersonIcon } from '@mui/icons-material';
import palette from 'src/theme/palette';
import PropertyIcon from '@mui/icons-material/Home';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import { Close as CloseIcon } from '@mui/icons-material';
import useMediaQuery from '@mui/material/useMediaQuery';

const ViewReviewModal = ({ open, onClose, review }) => {
  const isMobile = useMediaQuery('(max-width:600px)');
  const property = review?.property || {};
  const renderStars = (rating) => {
    const numRating = Number(rating) || 0;
    return (
      <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
        {[...Array(5)].map((_, i) => (
          <StarIcon
            key={i}
            sx={{
              fontSize: 20,
              color: i < numRating ? palette.warning.main : palette.grey[300]
            }}
          />
        ))}
        <Typography sx={{ fontFamily: 'poppinsMedium', ml: 1, color: palette.text.primary }}>
          {numRating}/5
        </Typography>
      </Box>
    );
  };

  const getStatusColor = (status) => {
    return status === 'active' ? 'success' : 'primary';
  };

  const formatWebsite = (website) => {
    if (!website) return '-';
    return website.startsWith('http') ? website : `https://${website}`;
  };
  const formatPrice = () => {
    if (!property.price) return 'N/A';

    if (property.is_area_unit === '1' && property.area_unit) {
      return `$${property.price.toLocaleString()} per ${property.area_unit.name.toLowerCase()}`;
    } else {
      return `$${property.price.toLocaleString()}`;
    }
  };
  const formatArea = () => {
    if (!property.area_size) return 'N/A';

    const area = property.area_size.toLocaleString();
    if (property.area_unit) {
      return `${area} ${property.area_unit.name.toLowerCase()}`;
    }
    return `${area} `;
  };
  if (!review) return null;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullScreen={isMobile}
      PaperProps={{
        sx: {
          borderRadius: isMobile ? 0 : 2,
          boxShadow: '0 10px 40px rgba(0,0,0,0.1)',
          overflow: 'hidden',
          maxHeight: isMobile ? '100vh' : '90vh',
          width: isMobile ? '100vw' : '1200px',
          margin: isMobile ? 0 : undefined
        }
      }}
    >
      {/* Header */}
      <DialogTitle
        sx={{
          background: `linear-gradient(135deg, ${palette.primary.main} 0%, ${palette.primary.dark} 100%)`,
          color: 'white',
          py: 3,
          px: 4
        }}
      >
        <Box
          sx={{ display: 'flex', alignItems: 'center', gap: 2, justifyContent: 'space-between' }}
        >
          {/* <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.2)', width: 50, height: 50 }}>
            <PersonIcon sx={{ fontSize: 30 }} />
          </Avatar> */}
          <Box>
            <Typography variant="h5" sx={{ fontFamily: 'poppinsSemiBold', mb: 0.5 }}>
              Review Details
            </Typography>
            <Typography sx={{ opacity: 0.9, fontFamily: 'poppinsRegular' }}>
              Complete review information
            </Typography>
          </Box>
          <IconButton
            onClick={onClose}
            sx={{
              color: palette.primary.white,
              '&:hover': {
                backgroundColor: palette.primary.dark
              }
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      {/* Content */}
      <DialogContent sx={{ py: 4, px: 4 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4, mt: 2 }}>
          {/* User Information Card */}
          <Box
            sx={{
              p: 3,
              backgroundColor: palette.grey[50],
              borderRadius: 2,
              border: `1px solid ${palette.divider}`
            }}
          >
            <Typography
              variant="h6"
              sx={{ fontFamily: 'poppinsSemiBold', mb: 2, color: palette.primary.main }}
            >
              User Information
            </Typography>

            <Box
              sx={{
                display: 'flex',
                flexDirection: { xs: 'column', sm: 'row' },
                justifyContent: 'space-between',
                alignItems: { xs: 'flex-start', sm: 'center' },
                mb: 2
              }}
            >
              <Box sx={{ flex: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                  <Box>
                    <Typography
                      sx={{
                        fontFamily: 'poppinsSemiBold',
                        fontSize: '16px',
                        color: palette.text.primary
                      }}
                    >
                      {review?.name || 'N/A'}
                    </Typography>
                    <Typography
                      sx={{ fontFamily: 'poppinsRegular', color: palette.text.secondary, mt: 0.5 }}
                    >
                      {review?.email || 'N/A'}
                    </Typography>
                  </Box>
                </Box>

                <Box
                  sx={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: 2,
                    flexDirection: { xs: 'column', sm: 'row' }
                  }}
                >
                  <Box>
                    <Typography
                      sx={{
                        fontFamily: 'poppinsMedium',
                        color: palette.text.secondary,
                        fontSize: '14px'
                      }}
                    >
                      Status
                    </Typography>
                    <Chip
                      label={review.status?.toUpperCase()}
                      color={getStatusColor(review.status)}
                      size="small"
                      sx={{ mt: 0.5, fontFamily: 'poppinsMedium' }}
                    />
                  </Box>

                  <Box>
                    <Typography
                      sx={{
                        fontFamily: 'poppinsMedium',
                        color: palette.text.secondary,
                        fontSize: '14px'
                      }}
                    >
                      Posted
                    </Typography>
                    <Typography sx={{ fontFamily: 'poppinsRegular', mt: 0.5 }}>
                      {review?.published_at || 'N/A'}
                    </Typography>
                  </Box>
                </Box>
              </Box>

              <Box sx={{ textAlign: { xs: 'left', sm: 'center' } }}>
                <Typography
                  sx={{ fontFamily: 'poppinsMedium', color: palette.text.secondary, mb: 1 }}
                >
                  Rating
                </Typography>
                {renderStars(review?.rating)}
              </Box>
            </Box>
          </Box>

          {/* Property Details Section */}
          <Grid item xs={12}>
            <Box>
              <Typography
                variant="subtitle2"
                sx={{
                  color: palette.text.secondary,
                  fontFamily: 'poppinsMedium',
                  mb: 2,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1
                }}
              >
                <PropertyIcon color="primary" fontSize="small" />
                Property Details
              </Typography>

              <Box
                sx={{
                  display: 'flex',
                  gap: 3,
                  alignItems: 'flex-start',
                  flexDirection: { xs: 'column', sm: 'row' }
                }}
              >
                {/* Property Image */}
                <Box
                  sx={{
                    width: { xs: '100%', sm: 120 },
                    height: { xs: 200, sm: 120 },
                    borderRadius: 2,
                    overflow: 'hidden',
                    flexShrink: 0,
                    backgroundColor: palette.grey[200]
                  }}
                >
                  {property?.main_image ? (
                    <img
                      src={property?.main_image}
                      alt={property?.title}
                      style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover'
                      }}
                      onError={(e) => {
                        e.target.style.display = 'none';
                        e.target.nextSibling.style.display = 'flex';
                      }}
                    />
                  ) : null}
                  <Box
                    sx={{
                      width: '100%',
                      height: '100%',
                      display: property.main_image ? 'none' : 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      backgroundColor: palette.grey[300]
                    }}
                  >
                    <Typography variant="body2" color="textSecondary">
                      No Image
                    </Typography>
                  </Box>
                </Box>

                {/* Property Info */}
                <Box sx={{ flex: 1, width: '100%' }}>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      justifyContent: 'space-between',
                      flexDirection: { xs: 'column', sm: 'row' },
                      mb: 1
                    }}
                  >
                    <Typography
                      sx={{
                        fontFamily: 'poppinsSemiBold',
                        fontSize: '18px',
                        color: palette.text.primary,
                        lineHeight: 1.2
                      }}
                    >
                      {property.title || 'N/A'}
                    </Typography>

                    {/* Property Link Button */}
                    {property.property_link && (
                      <Button
                        component="a"
                        href={property.property_link}
                        target="_blank"
                        rel="noopener noreferrer"
                        size="small"
                        endIcon={<OpenInNewIcon />}
                        sx={{
                          textTransform: 'none',
                          fontFamily: 'poppinsMedium',
                          fontSize: '12px',
                          minWidth: 'auto'
                        }}
                      >
                        View Property
                      </Button>
                    )}
                  </Box>

                  {property.address && (
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <LocationOnIcon
                        sx={{ fontSize: 16, color: palette.text.secondary, mr: 0.5 }}
                      />
                      <Typography
                        sx={{
                          fontSize: '14px',
                          color: palette.text.secondary,
                          lineHeight: 1.2
                        }}
                      >
                        {property.address}
                      </Typography>
                    </Box>
                  )}

                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, gap: 1 }}>
                    <Typography
                      sx={{
                        fontSize: '13px',
                        color: palette.text.disabled,
                        lineHeight: 1.2
                      }}
                    >
                      {property.city || 'N/A'}
                      {property.state && `, ${property.state}`}
                    </Typography>
                  </Box>

                  {/* Property Stats */}
                  <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                    {property.price && (
                      <Chip label={formatPrice()} variant="outlined" color="primary" size="small" />
                    )}
                    {property.bedrooms && (
                      <Chip label={`${property.bedrooms} Beds`} variant="outlined" size="small" />
                    )}
                    {property.bathrooms && (
                      <Chip label={`${property.bathrooms} Baths`} variant="outlined" size="small" />
                    )}
                    {property.area_size && (
                      <Chip label={formatArea()} variant="outlined" size="small" />
                    )}
                  </Box>
                </Box>
              </Box>
            </Box>
          </Grid>

          {/* Website Information */}
          {review.website && (
            <Box>
              <Typography
                sx={{ fontFamily: 'poppinsSemiBold', mb: 2, color: palette.primary.main }}
              >
                Website
              </Typography>
              <Box
                sx={{
                  p: 2,
                  backgroundColor: palette.background.paper,
                  borderRadius: 2,
                  border: `1px solid ${palette.divider}`
                }}
              >
                <a
                  href={formatWebsite(review.website)}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    textDecoration: 'none',
                    color: palette.primary.main,
                    fontFamily: 'poppinsMedium',
                    fontSize: '15px',
                    wordBreak: 'break-all'
                  }}
                >
                  {review.website}
                </a>
              </Box>
            </Box>
          )}

          {/* Review Content */}
          <Box>
            <Typography
              variant="h6"
              sx={{ fontFamily: 'poppinsSemiBold', mb: 2, color: palette.primary.main }}
            >
              Review Content
            </Typography>
            <Box
              sx={{
                p: 3,
                backgroundColor: palette.background.paper,
                borderRadius: 2,
                border: `1px solid ${palette.divider}`,
                minHeight: 120
              }}
            >
              <Typography
                sx={{
                  fontFamily: 'poppinsRegular',
                  lineHeight: 1.7,
                  whiteSpace: 'pre-wrap',
                  color: palette.text.primary,
                  fontSize: '15px'
                }}
              >
                {review.review || 'No review text provided.'}
              </Typography>
            </Box>
          </Box>
        </Box>
      </DialogContent>

      {/* Actions */}
      <DialogActions
        sx={{
          borderTop: `1px solid ${palette.divider}`,
          py: 3,
          px: 4,
          gap: 2
        }}
      >
        <Button
          onClick={onClose}
          variant="contained"
          sx={{
            backgroundColor: palette.primary.main,
            color: 'white',
            px: 4,
            py: 1,
            '&:hover': {
              backgroundColor: palette.primary.dark
            },
            fontFamily: 'poppinsMedium',
            borderRadius: 1
          }}
        >
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ViewReviewModal;
